import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import { useSEO } from '../../hooks/useSEO';
import { SITE_LOGO } from '../../constants/assets';

interface Partner {
  id: string;
  name: string;
  logo_url?: string;
}

interface PartnerService {
  id: string;
  name: string;
  price: number;
}

interface SelectedService extends PartnerService {
  quantity: number;
}

export default function RegistroArregloPage() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [services, setServices] = useState<PartnerService[]>([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedServices, setSelectedServices] = useState<SelectedService[]>([]);
  const [showSummary, setShowSummary] = useState(false);
  
  const [formData, setFormData] = useState({
    partner_id: '',
    client_name: 'Cliente Genérico',
    description: ''
  });

  useSEO({
    title: 'Registro de Arreglo | Anthony Sánchez Couture',
    description: 'Formulario rápido para registro de arreglos y servicios.',
    robots: 'noindex, nofollow'
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    const { data } = await supabase.from('partners').select('id, name, logo_url').order('name');
    if (data) setPartners(data);
  };

  const fetchServices = async (partnerId: string) => {
    const { data } = await supabase
      .from('partner_services')
      .select('id, name, price')
      .eq('partner_id', partnerId)
      .order('name');
    if (data) setServices(data);
  };

  const handlePartnerChange = (id: string) => {
    setFormData(prev => ({ ...prev, partner_id: id }));
    setSelectedServices([]);
    setSearchTerm('');
    if (id) fetchServices(id);
    else setServices([]);
  };

  const filteredServices = useMemo(() => {
    if (!searchTerm) return services;
    const lowerSearch = searchTerm.toLowerCase();
    return services.filter(s => s.name.toLowerCase().includes(lowerSearch));
  }, [services, searchTerm]);

  const toggleService = (service: PartnerService) => {
    setSelectedServices(prev => {
      const existing = prev.find(s => s.id === service.id);
      if (existing) {
        return prev.filter(s => s.id !== service.id);
      } else {
        return [...prev, { ...service, quantity: 1 }];
      }
    });
  };

  const updateQuantity = (id: string, delta: number) => {
    setSelectedServices(prev => prev.map(s => {
      if (s.id === id) {
        return { ...s, quantity: Math.max(1, s.quantity + delta) };
      }
      return s;
    }));
  };

  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setLoading(true);
    setSuccess(false);
    setError(null);

    const allAlterations: any[] = [];
    
    selectedServices.forEach(service => {
      const perServiceAlterations = Array.from({ length: service.quantity }).map(() => ({
        partner_id: formData.partner_id,
        service_name: service.name,
        price: service.price,
        client_name: formData.client_name,
        client_email: '',
        client_phone: '',
        description: formData.description,
        image_url: '',
        status: 'in_progress',
        created_at: new Date().toISOString()
      }));
      allAlterations.push(...perServiceAlterations);
    });

    const { error: dbError } = await supabase.from('alterations').insert(allAlterations);

    if (dbError) {
      console.error('Error inserting alterations:', dbError);
      setError(dbError.message);
      setShowSummary(false);
    } else {
      setSuccess(true);
      setSelectedServices([]);
      setFormData(prev => ({ ...prev, description: '' }));
      setShowSummary(false);
      setSearchTerm('');
      setTimeout(() => setSuccess(false), 3000);
    }
    setLoading(false);
  };

  const totalPrice = useMemo(() => {
    return selectedServices.reduce((sum, s) => sum + (s.price * s.quantity), 0);
  }, [selectedServices]);

  const partnerName = partners.find(p => p.id === formData.partner_id)?.name || 'Partner';

  return (
    <div className="min-h-screen bg-brand-gray-50 flex items-center justify-center p-4 sm:p-10 relative overflow-x-hidden">
      <div className="max-w-2xl w-full">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <img
            src={SITE_LOGO}
            alt="Anthony Sánchez Couture"
            className="h-10 w-auto object-contain mx-auto mb-4"
          />
          <h1 className="font-serif text-brand-black text-2xl font-medium">Registro de Arreglo</h1>
          <p className="font-sans text-brand-gray-500 text-sm mt-1">Formulario inteligente para partners</p>
        </div>

        <div className="bg-white rounded-[32px] border border-brand-gray-200 p-6 sm:p-8 shadow-xl shadow-brand-black/5 mb-20">
          <div className="space-y-6">
            {/* Selección de Partner */}
            <div className="space-y-2">
              <label className="font-sans text-[10px] uppercase tracking-[0.2em] text-brand-gray-400 font-bold ml-1">
                Marca / Partner
              </label>
              <select
                required
                className="w-full bg-brand-gray-50 border border-brand-gray-100 rounded-2xl px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-sage/30 transition-all appearance-none cursor-pointer"
                value={formData.partner_id}
                onChange={(e) => handlePartnerChange(e.target.value)}
              >
                <option value="">Selecciona una marca...</option>
                {partners.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            {/* Lista de Tarifas/Servicios con Buscador */}
            <AnimatePresence mode="wait">
              {formData.partner_id && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <label className="font-sans text-[10px] uppercase tracking-[0.2em] text-brand-gray-400 font-bold ml-1">
                      Servicios ({selectedServices.length} seleccionados)
                    </label>
                  </div>

                  {/* Buscador */}
                  <div className="relative">
                    <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-brand-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar servicio..."
                      className="w-full bg-brand-gray-50 border border-brand-gray-100 rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-black/5 transition-all"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-2 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                    {filteredServices.length > 0 ? (
                      filteredServices.map(service => {
                        const selectedItem = selectedServices.find(s => s.id === service.id);
                        const isSelected = !!selectedItem;
                        return (
                          <div
                            key={service.id}
                            className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all group ${
                              isSelected
                                ? 'bg-brand-black border-brand-black text-white'
                                : 'bg-white border-brand-gray-100 text-brand-gray-600 hover:border-brand-black/20 hover:bg-brand-gray-50'
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() => toggleService(service)}
                              className="flex items-center gap-3 flex-1 text-left"
                            >
                              <div className={`w-5 h-5 rounded-md flex items-center justify-center border transition-all ${
                                isSelected ? 'bg-brand-sage border-brand-sage' : 'bg-white border-brand-gray-200'
                              }`}>
                                {isSelected && <i className="ri-check-line text-white text-xs" />}
                              </div>
                              <div className="flex flex-col">
                                <span className="font-sans text-sm font-medium">{service.name}</span>
                                <span className={`text-[10px] ${isSelected ? 'text-brand-sage' : 'text-brand-gray-400'}`}>{service.price}€</span>
                              </div>
                            </button>
                            
                            {isSelected && (
                              <div className="flex items-center gap-3 ml-4 bg-white/10 rounded-lg p-1">
                                <button
                                  type="button"
                                  onClick={() => updateQuantity(service.id, -1)}
                                  className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-white/20 transition-all"
                                >
                                  <i className="ri-subtract-line text-sm" />
                                </button>
                                <span className="w-4 text-center font-bold text-xs">{selectedItem.quantity}</span>
                                <button
                                  type="button"
                                  onClick={() => updateQuantity(service.id, 1)}
                                  className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-white/20 transition-all"
                                >
                                  <i className="ri-add-line text-sm" />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-6 bg-brand-gray-50 rounded-xl border border-dashed border-brand-gray-200">
                        <p className="text-sm text-brand-gray-400 italic font-sans">
                          {searchTerm ? 'No hay resultados para tu búsqueda' : 'Esta marca no tiene tarifas asignadas.'}
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Información Adicional */}
            <div className="space-y-2">
              <label className="font-sans text-[10px] uppercase tracking-[0.2em] text-brand-gray-400 font-bold ml-1">
                Nota / Detalle (Opcional)
              </label>
              <input
                type="text"
                className="w-full bg-brand-gray-50 border border-brand-gray-100 rounded-xl px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-sage/30 transition-all"
                placeholder="Ej: Ajuste de largo, cambio de cremallera..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="pt-4">
              <button
                type="button"
                onClick={() => setShowSummary(true)}
                disabled={loading || selectedServices.length === 0}
                className={`w-full py-4 rounded-full font-sans text-xs tracking-[0.2em] uppercase transition-all flex items-center justify-center gap-3 font-bold ${
                  loading || selectedServices.length === 0
                    ? 'bg-brand-gray-100 text-brand-gray-400 cursor-not-allowed'
                    : 'bg-brand-black text-white hover:bg-brand-gray-800 shadow-xl active:scale-[0.98]'
                }`}
              >
                Revisar y Registrar ({selectedServices.length})
              </button>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="bg-red-50 border border-red-100 text-red-600 p-4 rounded-2xl text-center flex items-center justify-center gap-3"
                >
                  <i className="ri-error-warning-fill text-xl text-red-500" />
                  <span className="font-sans text-sm font-medium">Error: {error}</span>
                </motion.div>
              )}

              {success && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="bg-brand-sage/10 border border-brand-sage/20 text-brand-black p-4 rounded-2xl text-center flex items-center justify-center gap-3"
                >
                  <i className="ri-checkbox-circle-fill text-xl text-brand-sage" />
                  <span className="font-sans text-sm font-medium">¡Arreglos registrados correctamente!</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Footer info */}
        <div className="mt-8 text-center pb-10">
          <p className="font-sans text-brand-gray-400 text-[10px] uppercase tracking-widest">
            Atelier Anthony Sánchez Couture
          </p>
        </div>
      </div>

      {/* Floating Summary Bar */}
      {selectedServices.length > 0 && !showSummary && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          exit={{ y: 100 }}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[90%] max-w-md bg-brand-black text-white rounded-2xl p-4 shadow-2xl flex items-center justify-between z-40 border border-white/10"
        >
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wider text-white/50">Total estimado</span>
            <span className="text-lg font-bold text-brand-sage">{totalPrice}€</span>
          </div>
          <button
            onClick={() => setShowSummary(true)}
            className="bg-brand-sage text-brand-black px-6 py-2 rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-white transition-colors"
          >
            Confirmar
          </button>
        </motion.div>
      )}

      {/* Confirmation Modal */}
      <AnimatePresence>
        {showSummary && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowSummary(false)}
              className="absolute inset-0 bg-brand-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-[32px] w-full max-w-lg relative z-10 overflow-hidden shadow-2xl"
            >
              <div className="bg-brand-black p-6 text-white flex items-center justify-between">
                <div>
                  <h2 className="font-serif text-xl">Resumen del Registro</h2>
                  <p className="text-white/60 text-xs mt-1">Marca: {partnerName}</p>
                </div>
                <button onClick={() => setShowSummary(false)} className="text-white/50 hover:text-white transition-colors">
                  <i className="ri-close-line text-2xl" />
                </button>
              </div>
              
              <div className="p-6 max-h-[50vh] overflow-y-auto custom-scrollbar">
                <div className="space-y-4">
                  {selectedServices.map(service => (
                    <div key={service.id} className="flex items-center justify-between py-3 border-b border-brand-gray-100 last:border-0">
                      <div>
                        <p className="font-sans text-sm font-medium text-brand-black">{service.name}</p>
                        <p className="font-sans text-[10px] text-brand-gray-400">Precio unitario: {service.price}€</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-sans text-sm font-bold text-brand-black">{service.price * service.quantity}€</p>
                          <p className="font-sans text-[10px] text-brand-gray-400">Cantidad: {service.quantity}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {formData.description && (
                  <div className="mt-6 p-4 bg-brand-gray-50 rounded-xl">
                    <p className="text-[10px] uppercase tracking-wider text-brand-gray-400 font-bold mb-1">Nota adicional</p>
                    <p className="text-sm text-brand-gray-600 italic">"{formData.description}"</p>
                  </div>
                )}
              </div>

              <div className="p-6 bg-brand-gray-50 border-t border-brand-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <span className="font-serif text-lg text-brand-black">Total a registrar</span>
                  <span className="font-sans text-2xl font-bold text-brand-black">{totalPrice}€</span>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowSummary(false)}
                    className="flex-1 py-3 rounded-xl border border-brand-gray-200 text-brand-gray-600 font-bold text-xs uppercase tracking-wider hover:bg-brand-gray-100 transition-all"
                  >
                    Volver
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="flex-2 py-3 px-8 rounded-xl bg-brand-black text-white font-bold text-xs uppercase tracking-wider hover:bg-brand-gray-800 transition-all flex items-center justify-center gap-2"
                  >
                    {loading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-check-line" />}
                    Confirmar Registro
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e2e2e2;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #d1d1d1;
        }
      `}</style>
    </div>
  );
}
